import { useState } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, TextField, InputAdornment,
  TablePagination, IconButton, Collapse, Chip, Checkbox
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { DCManagedItem, DCDemandMap } from '../../types/dc.types'

interface ManagedItemsTableProps {
  items: DCManagedItem[]
  total: number
  page: number
  limit: number
  search: string
  demandMap: DCDemandMap | null
  qtyOverrides: Record<number, number>
  onQtyOverride: (medicineId: number, qty: number) => void
  onSearchChange: (search: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onRemove: (id: number) => void
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
}

export default function ManagedItemsTable({
  items, total, page, limit, search, demandMap, qtyOverrides, onQtyOverride,
  onSearchChange, onPageChange, onLimitChange, onRemove,
  selectedIds, onSelectionChange
}: ManagedItemsTableProps) {
  const [localSearch, setLocalSearch] = useState(search)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const handleSearchSubmit = () => {
    onSearchChange(localSearch)
  }

  const getDemand = (medicineId: number) => {
    if (!demandMap) return null
    return demandMap[String(medicineId)] || null
  }

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter(sid => sid !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  const handleToggleAll = () => {
    const currentPageIds = items.map(i => i.id)
    const allSelected = currentPageIds.every(id => selectedIds.includes(id))
    if (allSelected) {
      onSelectionChange(selectedIds.filter(id => !currentPageIds.includes(id)))
    } else {
      const newIds = [...new Set([...selectedIds, ...currentPageIds])]
      onSelectionChange(newIds)
    }
  }

  const allOnPageSelected = items.length > 0 && items.every(i => selectedIds.includes(i.id))

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search managed items..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
        {selectedIds.length > 0 && (
          <Chip
            label={`${selectedIds.length} selected`}
            color="primary"
            onDelete={() => onSelectionChange([])}
          />
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={selectedIds.length > 0 && !allOnPageSelected}
                  onChange={handleToggleAll}
                />
              </TableCell>
              <TableCell width={40} />
              <TableCell>Medicine</TableCell>
              <TableCell>Pack Size</TableCell>
              <TableCell>Best Distributor</TableCell>
              <TableCell align="right">Best Price</TableCell>
              <TableCell align="right">14d Demand</TableCell>
              <TableCell align="right">14d - Inv</TableCell>
              <TableCell>Method</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No managed items. Add items from the Eligible Items page.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const demand = getDemand(item.medicine_id)
                const isExpanded = expandedId === item.id

                const demand14d = demand ? Math.ceil(demand.total_demand_14d) : null
                const invAvailable = demand?.inventory_available ?? 0
                const netQty = demand14d !== null ? Math.max(0, demand14d - invAvailable) : null
                const isOverridden = qtyOverrides[item.medicine_id] !== undefined
                const displayQty = isOverridden
                  ? qtyOverrides[item.medicine_id]
                  : netQty

                return (
                  <>
                    <TableRow key={item.id} hover selected={selectedIds.includes(item.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleToggle(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.medicine_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.pack_size}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {item.best_distributor_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {item.best_offer_landing_cost
                          ? `Rs. ${item.best_offer_landing_cost.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {demand14d !== null ? demand14d : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {demand !== null ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={displayQty ?? ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value)
                                if (!isNaN(val) && val >= 0) {
                                  onQtyOverride(item.medicine_id, val)
                                }
                              }}
                              inputProps={{ min: 0, style: { width: 60, textAlign: 'right', padding: '4px 6px' } }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: 28,
                                  bgcolor: isOverridden ? 'warning.light' : undefined
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.disabled">
                              Inv: {invAvailable}{isOverridden ? ' · edited' : ''}
                            </Typography>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.added_method}
                          size="small"
                          color={item.added_method === 'auto' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => onRemove(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && demand && (
                      <TableRow key={`${item.id}-detail`}>
                        <TableCell colSpan={10} sx={{ py: 0 }}>
                          <Collapse in={isExpanded}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>Store Demand Breakdown</Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Store</TableCell>
                                    <TableCell align="right">Avg Daily Sales</TableCell>
                                    <TableCell align="right">14d Demand</TableCell>
                                    <TableCell align="right">Share %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {demand.stores.map((s) => (
                                    <TableRow key={s.store_id}>
                                      <TableCell>{s.store_name}</TableCell>
                                      <TableCell align="right">{s.avg_daily_sales.toFixed(2)}</TableCell>
                                      <TableCell align="right">{s.demand_14d.toFixed(1)}</TableCell>
                                      <TableCell align="right">{s.share_pct.toFixed(1)}%</TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow>
                                    <TableCell><strong>Total</strong></TableCell>
                                    <TableCell align="right"><strong>{demand.total_avg_daily.toFixed(2)}</strong></TableCell>
                                    <TableCell align="right"><strong>{demand.total_demand_14d.toFixed(1)}</strong></TableCell>
                                    <TableCell align="right"><strong>100%</strong></TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={limit}
        onPageChange={(_, p) => onPageChange(p + 1)}
        onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value))}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  )
}
